import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Crown, Zap, Star, Heart, BookOpen, Lock, Sparkles, ShoppingBag } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const ALL_PRODUCTS = [
  // Premium Plans
  {
    id: 'premium_monthly',
    category: 'premium',
    title: 'Premium Mensal',
    price: 'R$ 29,90',
    period: '/mês',
    description: 'Gamificação + 10 Atividades Diárias + Sementes NFT',
    stripeLink: 'https://buy.stripe.com/6oUcN5dew8MP1jc7u38Vi06',
    benefits: ['10 Atividades Diárias', 'Badge Premium', 'Jardim da Alma ilimitado', 'Sementes NFT', 'Sem anúncios'],
    icon: Zap,
    accentColor: '#818cf8',
    popular: false,
  },
  {
    id: 'premium_quarterly',
    category: 'premium',
    title: 'Premium Trimestral',
    price: 'R$ 79,90',
    period: '/3 meses',
    originalPrice: 'R$ 89,70',
    description: 'Economia de 11% — Compromisso com sua recuperação',
    stripeLink: 'https://buy.stripe.com/28E28r1vO8MP4vobKj8Vi05',
    benefits: ['10 Atividades Diárias', 'Badge Premium', 'Jardim da Alma ilimitado', 'Sementes NFT', 'Sem anúncios', '11% de economia'],
    icon: Star,
    accentColor: '#fbbf24',
    popular: true,
  },
  {
    id: 'premium_annual',
    category: 'premium',
    title: 'Premium Anual',
    price: 'R$ 249,90',
    period: '/ano',
    originalPrice: 'R$ 358,80',
    description: 'Melhor valor — 30% de economia',
    stripeLink: 'https://buy.stripe.com/8x23cvgqId353rk01B8Vi04',
    benefits: ['10 Atividades Diárias', 'Badge Premium', 'Jardim da Alma ilimitado', 'Sementes NFT', 'Sem anúncios', '30% de economia', 'Prioridade em novos recursos'],
    icon: Crown,
    accentColor: '#a78bfa',
    popular: false,
  },
  // Founder Plans
  {
    id: 'founder-90',
    category: 'fundador',
    title: 'Fundador 90 dias',
    price: 'R$ 145,00',
    period: '',
    originalPrice: 'R$ 350,00',
    description: 'Metodologia Enantiodromia — Recuperação Profunda',
    stripeLink: 'https://buy.stripe.com/6oUfZhb6o0gje5Y15F8Vi01',
    benefits: ['Programa de Apoio completo', 'Metodologia Enantiodromia', 'Badge Fundador exclusivo', 'Ganho 2x tokens AWA', 'Grupos exclusivos', 'Suporte prioritário'],
    icon: Sparkles,
    accentColor: '#f97316',
    popular: false,
  },
  {
    id: 'founder-180',
    category: 'fundador',
    title: 'Fundador 180 dias',
    price: 'R$ 320,00',
    period: '',
    originalPrice: 'R$ 669,00',
    description: 'Melhor Custo-Benefício — Recuperação Completa',
    stripeLink: 'https://buy.stripe.com/cNi5kD3DWd352ng9Cb8Vi00',
    benefits: ['Programa de Apoio completo', 'Metodologia Enantiodromia', 'Badge Fundador exclusivo', 'Ganho 2x tokens AWA', 'Grupos exclusivos', 'Suporte prioritário', 'MELHOR CUSTO-BENEFÍCIO'],
    icon: Crown,
    accentColor: '#f59e0b',
    popular: true,
  },
  // Family Plan
  {
    id: 'course-family',
    category: 'familiar',
    title: 'Programa para Familiares',
    price: 'R$ 250,00',
    period: '',
    description: 'Apoio e Orientação — 90 dias de programa estruturado',
    stripeLink: 'https://buy.stripe.com/00w28rdewd352ng5lV8Vi02',
    benefits: ['Programa completo de 90 dias', 'Técnicas de apoio familiar', 'Suporte emocional estruturado', 'Estratégias de comunicação', 'Certificado de conclusão', 'Acesso vitalício ao material'],
    icon: Heart,
    accentColor: '#f472b6',
    popular: false,
  },
];

function ProductCard({ product, onCheckout }) {
  const Icon = product.icon;
  return (
    <div
      className={`relative group rounded-2xl p-[1px] transition-all duration-300 hover:scale-[1.02] ${
        product.popular ? 'shadow-2xl' : 'shadow-lg hover:shadow-xl'
      }`}
      style={{
        background: product.popular
          ? `linear-gradient(135deg, ${product.accentColor}88, ${product.accentColor}44, transparent)`
          : `linear-gradient(135deg, ${product.accentColor}33, transparent)`,
      }}
      data-testid={`plan-card-${product.id}`}
    >
      <div className="relative rounded-2xl p-6 h-full flex flex-col" style={{ background: 'rgba(15,15,35,0.95)', backdropFilter: 'blur(16px)' }}>
        {product.popular && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
            <span className="px-4 py-1 text-xs font-bold rounded-full text-gray-900" style={{ background: product.accentColor }}>
              MAIS ESCOLHIDO
            </span>
          </div>
        )}

        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${product.accentColor}22` }}>
            <Icon className="h-5 w-5" style={{ color: product.accentColor }} />
          </div>
          <div>
            <h3 className="text-base font-bold text-white">{product.title}</h3>
            <p className="text-xs text-gray-500">{product.description}</p>
          </div>
        </div>

        <div className="mb-4">
          <span className="text-2xl font-bold text-white">{product.price}</span>
          {product.period && <span className="text-gray-500 text-sm">{product.period}</span>}
          {product.originalPrice && (
            <span className="ml-2 text-sm text-gray-600 line-through">{product.originalPrice}</span>
          )}
        </div>

        <ul className="space-y-2 flex-1 mb-5">
          {product.benefits.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
              <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: product.accentColor }} />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <button
          onClick={() => onCheckout(product.stripeLink, product.title)}
          className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:brightness-110"
          style={{ background: `linear-gradient(135deg, ${product.accentColor}, ${product.accentColor}cc)` }}
          data-testid={`subscribe-${product.id}`}
        >
          Assinar Agora
        </button>
      </div>
    </div>
  );
}

export function Checkout() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');

  const isMasterUser = user && (user.is_premium || user.is_founder);

  const handleCheckout = (stripeLink, planName) => {
    if (isMasterUser) {
      toast.info(`Modo bypass ativo. Link do Stripe: ${planName}`);
      window.open(stripeLink, '_blank');
      return;
    }
    toast.info(`Redirecionando para pagamento: ${planName}`);
    window.location.href = stripeLink;
  };

  // Auth loading
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #06061a 0%, #0c0c2d 50%, #0a0a1a 100%)' }} data-testid="checkout-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400 mx-auto mb-4"></div>
          <p className="text-slate-400">Verificando seu acesso...</p>
        </div>
      </div>
    );
  }

  // Redirect to login
  if (!authLoading && !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #06061a 0%, #0c0c2d 50%, #0a0a1a 100%)' }}>
        <div className="text-center">
          <Lock className="h-12 w-12 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-400 mb-4">Faça login para acessar a loja</p>
          <button onClick={() => navigate('/login')} className="px-6 py-2 rounded-xl bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition-colors">
            Fazer Login
          </button>
        </div>
      </div>
    );
  }

  const categories = [
    { key: 'all', label: 'Todos' },
    { key: 'premium', label: 'Premium' },
    { key: 'fundador', label: 'Fundador' },
    { key: 'familiar', label: 'Familiar' },
  ];

  const filteredProducts = filter === 'all' ? ALL_PRODUCTS : ALL_PRODUCTS.filter(p => p.category === filter);

  return (
    <div className="min-h-screen py-10 px-4" style={{ background: 'linear-gradient(180deg, #06061a 0%, #0c0c2d 50%, #0a0a1a 100%)' }} data-testid="shop-page">
      <div className="container max-w-6xl mx-auto">
        {/* Master Banner */}
        {isMasterUser && (
          <div className="mb-6 p-4 rounded-xl flex items-center gap-3" style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)' }} data-testid="master-banner">
            <Crown className="h-5 w-5 text-purple-400 flex-shrink-0" />
            <p className="text-sm text-purple-300">Acesso Master ativo. Sistema de pagamento em modo bypass para sua conta.</p>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-3">
            <ShoppingBag className="h-8 w-8 text-purple-400" />
            <h1 className="text-3xl sm:text-4xl font-bold text-white">Loja U-Clean</h1>
          </div>
          <p className="text-gray-400 max-w-xl mx-auto text-sm sm:text-base">
            Apoie a U-Clean e desbloqueie recursos exclusivos para sua jornada de recuperação
          </p>
          <p className="text-xs text-gray-600 mt-2">
            A U-Clean é e sempre será uma rede social gratuita. Planos pagos são opcionais.
          </p>
        </div>

        {/* Filter pills */}
        <div className="flex justify-center gap-2 mb-8 flex-wrap" data-testid="shop-filters">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setFilter(cat.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                filter === cat.key
                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/25'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-gray-300'
              }`}
              data-testid={`filter-${cat.key}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Product grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} onCheckout={handleCheckout} />
          ))}
        </div>

        {/* Book CTA */}
        <div className="rounded-2xl p-[1px] max-w-3xl mx-auto mb-10" style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.4), rgba(59,130,246,0.3), transparent)' }}>
          <div className="rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6" style={{ background: 'rgba(15,15,35,0.95)', backdropFilter: 'blur(16px)' }}>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(168,85,247,0.15)' }}>
              <BookOpen className="h-8 w-8 text-purple-400" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="text-lg font-bold text-white mb-1">Reconstruindo Caminhos</h3>
              <p className="text-sm text-gray-400">Guia prático baseado na filosofia da Enantiodromia para familiares e pessoas em recuperação.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => window.open('https://amazon.com/dp/B0F6P16L19', '_blank')} className="px-4 py-2 rounded-xl text-xs font-semibold text-white transition-colors" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }} data-testid="buy-amazon-btn">
                Amazon
              </button>
              <button onClick={() => window.open('https://go.hotmart.com/J97516427X?dp=1', '_blank')} className="px-4 py-2 rounded-xl text-xs font-semibold text-white transition-colors" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }} data-testid="buy-hotmart-btn">
                Hotmart
              </button>
            </div>
          </div>
        </div>

        {/* Trust footer */}
        <div className="text-center text-xs text-gray-600">
          <p>Pagamento 100% seguro via Stripe · Criptografia SSL · Todas as bandeiras aceitas</p>
        </div>
      </div>
    </div>
  );
}
